import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { DEFAULT_PREPROCESSING, type PreprocessingParams } from "@tbm/shared";
import { api } from "../lib/api";

interface PreprocessingStore extends PreprocessingParams {
  set: <K extends keyof PreprocessingParams>(key: K, value: PreprocessingParams[K]) => void;
  /** Bulk-apply a partial — used to hydrate the globally-saved subset. */
  hydrate: (partial: Partial<PreprocessingParams>) => void;
  reset: () => void;
}

export const usePreprocessing = create<PreprocessingStore>((set) => ({
  ...DEFAULT_PREPROCESSING,
  set: (key, value) => set({ [key]: value } as Partial<PreprocessingStore>),
  hydrate: (partial) => set(partial as Partial<PreprocessingStore>),
  reset: () => set({ ...DEFAULT_PREPROCESSING }),
}));

/**
 * Seed the globally-saved preprocessing subset (every drawer section
 * except Aggregation) from the drive record, once, on app start.
 * Aggregation stays session-local — never hydrated, never persisted.
 *
 * Returns whether hydration has settled. Callers gate their first paint
 * on it: the data queries key off the preprocessing params, so mounting
 * before hydration issued every query twice — once with the defaults,
 * then again with the operator's saved values.
 *
 * `true` is also returned when the drive query has failed outright, so
 * a backend problem degrades to the defaults rather than a hung screen.
 */
export function useHydrateSavedPreprocessing(): boolean {
  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });
  const data = drive.data;
  const done = useRef(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (done.current) return;
    if (data) {
      done.current = true;
      usePreprocessing.getState().hydrate(data.preprocessingGlobal);
      setReady(true);
    } else if (drive.isError) {
      done.current = true;
      setReady(true);
    }
  }, [data, drive.isError]);
  return ready;
}

/**
 * Returns a stable snapshot of the preprocessing params for use as an API
 * query dependency. `useShallow` compares field-by-field so we don't
 * re-render on every store update.
 */
export function usePreprocessingParams(): PreprocessingParams {
  return usePreprocessing(
    useShallow((s) => ({
      pipeOdMm: s.pipeOdMm,
      bulkingFactorBaseline: s.bulkingFactorBaseline,
      chainageAllowableIncreaseM: s.chainageAllowableIncreaseM,
      chainageAllowableDecreaseM: s.chainageAllowableDecreaseM,
      zeroFilter: s.zeroFilter,
      miningOnly: s.miningOnly,
      aggregation: s.aggregation,
      aggMethod: s.aggMethod,
      jackingRollingEnabled: s.jackingRollingEnabled,
      jackingRollingWindow: s.jackingRollingWindow,
      jackingRollingSigma: s.jackingRollingSigma,
      jackingMinKN: s.jackingMinKN,
    })),
  );
}

/**
 * Bulking-factor moving average — a linear-weighted moving-average
 * line drawn over the Bulking Factor trace on the Drive Parameters
 * plot, looking both forward and back from each point.
 *
 * Browser-local, like Aggregation: the controls live in the
 * Preprocessing drawer but the settings are never persisted to the
 * drive record or shared with other viewers. Session-scoped — they
 * reset on a hard refresh.
 */
export interface BulkingMaSettings {
  /** Draw the smoothed line. On by default. */
  enabled: boolean;
  /** Window reach behind each point, metres of chainage. */
  backM: number;
  /** Window reach ahead of each point, metres of chainage. */
  forwardM: number;
}

export const DEFAULT_BULKING_MA: BulkingMaSettings = {
  enabled: true,
  backM: 1,
  forwardM: 1,
};

interface BulkingMaStore extends BulkingMaSettings {
  set: <K extends keyof BulkingMaSettings>(
    key: K,
    value: BulkingMaSettings[K],
  ) => void;
  reset: () => void;
}

export const useBulkingMa = create<BulkingMaStore>((set) => ({
  ...DEFAULT_BULKING_MA,
  set: (key, value) => set({ [key]: value } as Partial<BulkingMaStore>),
  reset: () => set({ ...DEFAULT_BULKING_MA }),
}));
